import React, { useCallback, useMemo } from 'react';
import { View, Linking, Platform, Pressable } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import { addressToString } from '../../../common/utils';
import { Text } from '../../components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export const getLocationMapUrl = (location, platform = Platform.OS) => {
	const coordinates = location?.coordinates;
	const address = location ? addressToString(location.addressObj) : '';
	const pin = coordinates?.length === 2 ? coordinates.join(',') : '';
	const query = pin || address;
	if (!query) {
		return null;
	}

	const encodedQuery = encodeURIComponent(query);
	if (platform === 'ios') {
		return pin ? `maps://?ll=${pin}` : `maps://?q=${encodedQuery}`;
	}
	if (platform === 'android') {
		const label = location?.name ? `(${encodeURIComponent(location.name)})` : '';
		return pin ? `geo:${pin}?q=${pin}${label}` : `geo:0,0?q=${encodedQuery}`;
	}
	return `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
};

/** Opens coordinates, else the address, in the default maps app; Google Maps on web/failure. */
export const openLocationInMaps = async location => {
	const url = getLocationMapUrl(location);
	if (!url) {
		return;
	}
	try {
		await Linking.openURL(url);
	}
	catch {
		try {
			await Linking.openURL(getLocationMapUrl(location, 'web'));
		}
		catch {
			Toast.show('Unable to open maps. Try again.');
		}
	}
};

/**
 * Address of a location with a copy button. The address opens the maps app;
 * with asText it is plain text (pages that have their own Open in Maps button).
 */
export const MapLink = ({ location, asText = false }) => {
	const address = useMemo(() => (location ? addressToString(location.addressObj) : null), [location]);
	const { theme: { ui: { colors } } } = useTheme();
	const styles = useThemedStyles(createStyles);
	const coordinates = useMemo(() => location?.coordinates, [location]);

	const locationText = address || (coordinates?.length ? coordinates.join(', ') : '');
	const copyAddress = useCallback(async () => {
		if (!locationText) {
			return;
		}
		// Give immediate feedback after copying the address/coordinates
		await Clipboard.setStringAsync(locationText);
		Toast.show(`${locationText} copied`);
	}, [locationText]);

	const openMap = useCallback(() => openLocationInMaps(location), [location]);

	// A blank address record with no coordinates has nothing to show or copy as text.
	if ((!location?.coordinates?.length && !location?.addressObj) || (asText && !locationText)) {
		return null;
	}

	return (
		<View style={styles.row}>
			{asText ? (
				<Text selectable={true} style={styles.addressText}>
					{locationText}
				</Text>
			) : (
				<Pressable
					onPress={openMap}
					accessibilityRole="link"
					style={({ pressed }) => [styles.mapLink, pressed && styles.pressed]}
				>
					<Feather name="map-pin" size={18} color={colors.accent} />
					<Text style={styles.mapLabel}>{address || 'Go to map'}</Text>
				</Pressable>
			)}
			<Pressable
				onPress={copyAddress}
				accessibilityRole="button"
				accessibilityLabel="Copy address"
				style={({ pressed }) => [styles.copyButton, pressed && styles.pressed]}
			>
				<Feather name="copy" size={18} color={colors.iconMuted} />
			</Pressable>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, touch } }) => ({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
	},
	mapLink: {
		flex: 1,
		minHeight: touch.min,
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
	},
	mapLabel: {
		...type.bodySmallStrong,
		// Accent text with the pin icon, like the other links (no underline).
		color: colors.accent,
		flexShrink: 1,
	},
	addressText: {
		flex: 1,
		...type.rowTitle,
		color: colors.text,
		paddingVertical: space.sm,
	},
	copyButton: {
		width: touch.min,
		height: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
	},
	pressed: {
		opacity: 0.7,
	},
});
